import type { ButtonHTMLAttributes, ReactNode } from "react";

/**
 * Buttons in the student app's language, for the teacher's user-facing pages: a full pill, never
 * the admin glass. `solid` mirrors the app's call-to-action, `glass` the header pills it sits
 * among on the sky backdrop, and `quiet` the neutral actions inside a white card.
 */
const TONES = {
  solid: "bg-app-blue text-white shadow-app-card hover:bg-app-blue-dark",
  glass: "border border-white/70 bg-white/60 text-app-ink backdrop-blur hover:bg-white/80",
  quiet: "bg-app-sky-soft text-app-ink hover:bg-app-sky",
} as const;

const SIZES = {
  sm: "h-9 px-4 text-sm",
  md: "h-11 px-5 text-base",
} as const;

interface AppButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: keyof typeof TONES;
  size?: keyof typeof SIZES;
  isLoading?: boolean;
  children: ReactNode;
}

export function AppButton({
  tone = "solid",
  size = "md",
  isLoading = false,
  className = "",
  disabled,
  children,
  ...props
}: AppButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-full font-kumbh font-bold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-app-ink disabled:pointer-events-none disabled:opacity-45 ${TONES[tone]} ${SIZES[size]} ${className}`}
      {...props}
    >
      {isLoading && (
        <svg
          className="h-4 w-4 shrink-0 animate-spin"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </button>
  );
}
